import { AlertCircle } from 'lucide-react'
import { Label } from 'radix-ui'
import type {
  ChangeEventHandler,
  InputHTMLAttributes,
  ReactNode,
  TextareaHTMLAttributes,
} from 'react'
import { useId } from 'react'
import { cn } from '~/lib/utils'

const controlClass =
  'w-full rounded-control border bg-surface px-3 text-ink placeholder:text-ink-subtle ' +
  'shadow-inset transition-[border-color,box-shadow] duration-150 hover:border-ink-subtle ' +
  'focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 ' +
  'focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-60'

export interface FieldProps {
  label: string
  /** Plain-language guidance shown under the label, before the user makes a mistake. */
  hint?: string
  error?: string | undefined
  required?: boolean
  children: (id: string, describedBy: string | undefined) => ReactNode
}

/**
 * One labelled control with its hint and error.
 *
 * The error sits under the input and is wired through `aria-describedby`, so a
 * screen reader announces it with the field rather than as a detached banner. Hints
 * are phrased as help, never as a rule the user has already broken.
 *
 * It always renders the same four slots — label, hint, control, error — and spans
 * four rows as a subgrid, so side-by-side fields line their controls up even when
 * only one of them carries a hint. Absent slots are empty and contribute nothing
 * unless a neighbour fills the same track. Spacing therefore comes from margins
 * rather than a gap, which is what keeps a hintless row identical to a plain stack;
 * outside a grid, `subgrid` degrades to `none` and the margins still hold.
 *
 * The four-slot count is load-bearing: anything sharing a grid row with a Field has
 * to span four rows too, or it lands in the label track alone.
 */
export function Field({ label, hint, error, required, children }: FieldProps) {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined

  return (
    <div className="group row-span-4 grid grid-rows-subgrid gap-y-0">
      <Label.Root
        htmlFor={id}
        className="stencil mb-1.5 block text-ink-muted transition-colors group-focus-within:text-primary"
      >
        {label}
        {required ? <span className="ml-1 text-primary">*</span> : null}
      </Label.Root>
      {hint ? (
        <p id={hintId} className="mb-1.5 text-xs text-ink-muted">
          {hint}
        </p>
      ) : (
        <div />
      )}
      <div>{children(id, describedBy)}</div>
      {error ? (
        <p
          id={errorId}
          className="mt-1.5 flex items-start gap-1.5 text-xs font-medium text-danger"
          role="alert"
        >
          <AlertCircle className="mt-px size-3.5 shrink-0" aria-hidden />
          {error}
        </p>
      ) : (
        <div />
      )}
    </div>
  )
}

/**
 * A bare stencil label bound to whatever it wraps.
 *
 * `Field` is the full treatment — hint, error, `aria-describedby`. This is the
 * stripped-back version for controls that carry their own validation, and it exists
 * so no screen has to fall back to a wrapping `<label>`: nesting associates a label
 * with a native input only, and half of these controls are composite components.
 */
export function Labelled({
  label,
  required,
  className,
  children,
}: {
  label: string
  required?: boolean
  className?: string
  children: (id: string) => ReactNode
}) {
  const id = useId()
  return (
    <div className={className}>
      <Label.Root htmlFor={id} className="stencil mb-1.5 block text-ink-muted">
        {label}
        {required ? <span className="ml-1 text-primary">*</span> : null}
      </Label.Root>
      {children(id)}
    </div>
  )
}

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean
  prefix?: string
  /**
   * What this field will let somebody type. `inputMode` only picks the keyboard a
   * phone offers — see the note above `keepNumeric`, which is the actual rule.
   */
  accept?: AcceptedCharacters
}

/** The fixed part of a field — "+977", "%" — welded to its left edge. */
function withPrefix(input: ReactNode, prefix: string | undefined, invalid?: boolean) {
  if (!prefix) return input
  return (
    <div className="flex">
      <span
        className={cn(
          'inline-flex h-11 items-center rounded-l-control border border-r-0 px-3',
          'bg-surface-muted text-sm font-semibold text-ink-muted tnum',
          invalid ? 'border-danger' : 'border-border-strong',
        )}
      >
        {prefix}
      </span>
      {input}
    </div>
  )
}

export function TextInput({
  className,
  invalid,
  prefix,
  accept,
  onChange,
  ...props
}: TextInputProps) {
  return withPrefix(
    <input
      className={cn(
        controlClass,
        'h-11 text-base sm:text-sm',
        invalid ? 'border-danger' : 'border-border-strong',
        prefix && 'rounded-l-none border-l-0',
        className,
      )}
      aria-invalid={invalid || undefined}
      onChange={accept ? scrubbed(accept, onChange) : onChange}
      {...props}
    />,
    prefix,
    invalid,
  )
}

export function TextArea({
  className,
  invalid,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }) {
  return (
    <textarea
      className={cn(
        controlClass,
        'min-h-24 py-2.5 text-base sm:text-sm',
        invalid ? 'border-danger' : 'border-border-strong',
        className,
      )}
      aria-invalid={invalid || undefined}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// Fields that only accept a number
// ---------------------------------------------------------------------------

/**
 * `inputMode` picks the keyboard a phone offers. It is a hint — every desktop
 * keyboard ignores it — so a price field took "asdads", parsed it to null, and the
 * form looked filled in while the server was being sent nothing. These are the
 * actual rule.
 */

/**
 * Digits, and one decimal point where the unit has places to spend it.
 *
 * Grouping goes too, deliberately. It would be friendlier to let "1,000" stand —
 * `parseMoney` does strip commas — but `parseQuantity` does not, and neither does
 * the bare `Number()` behind a lead time or a validity window. Keeping the text
 * something *every* reader of it can parse is what makes "what is on screen is what
 * will be sent" true rather than nearly true.
 *
 * A trailing "12." survives on purpose: it is the only way to reach "12.5". A
 * second point folds into the fraction instead of truncating what follows it, so
 * "1.2.3" lands on "1.23" — the digits somebody meant, in the order they typed them.
 */
export function keepNumeric(text: string, decimals = 0): string {
  const kept = text.replace(decimals > 0 ? /[^\d.]/g : /\D/g, '')
  if (decimals === 0) return kept
  const [whole = '', ...rest] = kept.split('.')
  return rest.length === 0 ? whole : `${whole}.${rest.join('').slice(0, decimals)}`
}

/**
 * Digits and the punctuation people write phone numbers with. `normalizePhone`
 * throws all of it away, so "+977 98-1234-5678" and "9812345678" are the same
 * number and both are worth being able to type.
 */
export function keepPhone(text: string): string {
  return text.replace(/[^\d\s+\-()]/g, '')
}

export type AcceptedCharacters = 'digits' | 'phone'

const CLEANERS: Record<AcceptedCharacters, (text: string) => string> = {
  digits: (text) => keepNumeric(text),
  phone: keepPhone,
}

/**
 * Strip what should never have been typed, in the field, as it is typed.
 *
 * Done here rather than by cancelling `beforeinput`: React's `onBeforeInput` is a
 * synthetic of its own, and preventing it does not stop the browser inserting the
 * character. Rewriting `value` on the element works whether React owns it or — as
 * with a react-hook-form `register` — the DOM does, and it covers paste, drop and
 * autofill, none of which are keystrokes.
 *
 * The caret is put back by however much was removed, so editing the middle of a
 * number does not throw you to the end of it.
 */
function scrub(input: HTMLInputElement, clean: (text: string) => string): string {
  const cleaned = clean(input.value)
  if (cleaned === input.value) return cleaned
  const caret = (input.selectionStart ?? input.value.length) - (input.value.length - cleaned.length)
  input.value = cleaned
  input.setSelectionRange(caret, caret)
  return cleaned
}

/** `onChange` for an input whose value the DOM owns. */
export function scrubbed(
  accept: AcceptedCharacters,
  next?: ChangeEventHandler<HTMLInputElement>,
): ChangeEventHandler<HTMLInputElement> {
  return (event) => {
    scrub(event.currentTarget, CLEANERS[accept])
    next?.(event)
  }
}

export interface NumericInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'type'> {
  value: string
  onChange: (text: string) => void
  /** Decimal places this field accepts. Zero — the default — means whole numbers. */
  decimals?: number
  invalid?: boolean
  prefix?: string
}

/**
 * A number field that stays a number. Reports its text rather than a parsed value:
 * the screens own the parse, because what "empty" and "0" mean differs between
 * them, and half-typed states have to survive a re-render.
 */
export function NumericInput({
  value,
  onChange,
  decimals = 0,
  invalid,
  prefix,
  className,
  ...props
}: NumericInputProps) {
  return withPrefix(
    <input
      inputMode={decimals > 0 ? 'decimal' : 'numeric'}
      className={cn(
        controlClass,
        'h-11 text-base tnum sm:text-sm',
        invalid ? 'border-danger' : 'border-border-strong',
        prefix && 'rounded-l-none border-l-0',
        className,
      )}
      value={value}
      aria-invalid={invalid || undefined}
      onChange={(e) => onChange(scrub(e.currentTarget, (text) => keepNumeric(text, decimals)))}
      {...props}
    />,
    prefix,
    invalid,
  )
}
